import {RestService} from "@/services/rest/RestService";
import {EnumEndpointsApi as OrderEnumEndpointsApi} from "@/api/pharma-order-enum";
import {EnumEndpointsApi as MasterEnumEndpointsApi} from "@/api/pharma-master-enum";
import {EnumContentRestDtoModel} from "@/models/api/pharma-order-enum/EnumContentRestDtoModel";
import {MultilingualFieldRestDto} from "@/api/pharma-order-enum/models";
import {UpdateEnumContentRestDtoModel} from "@/models/api/pharma-order-enum/UpdateEnumContentRestDtoModel";
import {EnumClassNameCmsEnum} from "@/models/enum/EnumClassNameCmsEnum";
import {CacheService} from "@/services/cache/CacheService";
import {EnumClassNameCpcEnum} from "@/models/enum/EnumClassNameCpcEnum";
import {StatusEnum} from "@/api/enums/status-enum";
import {CustomerContext} from "@/context/CustomerContext";
import {AxiosResponse} from "axios";
import {CacheKeyEnum} from "@/services/cache/CacheKeyEnum";
import {DpModuleEnum} from "@/api/enums/dp-module-enum";
import {CodeDescriptionRestDto} from "@/api/pharma-master-enum/models";
import {EnumClassNamePimEnum} from "@/models/enum/EnumClassNamePimEnum";
import {EnumClassNameOrderEnum} from "@/models/enum/EnumClassNameOrderEnum";

export class EnumRestService extends RestService {

    private masterEnumApi: MasterEnumEndpointsApi;
    private orderEnumApi: OrderEnumEndpointsApi;

    protected constructor() {
        super();

        this.masterEnumApi = new MasterEnumEndpointsApi(this.getDpAxiosV2Instance(), undefined, this.getConfig().masterUrl);
        this.orderEnumApi = new OrderEnumEndpointsApi(this.getDpAxiosV2Instance(), undefined, this.getConfig().orderUrl);
    }

    public async getEnumValuesByClassName(enumClassName: string, moduleCode: string): Promise<CodeDescriptionRestDto[]> {
        let enumClassNameToUse: string = enumClassName;
        if (!enumClassName.startsWith("App\\") && !enumClassName.startsWith("DigitalPharma\\")) {
            enumClassNameToUse = "App\\Model\\Enum\\" + enumClassName;
        }
        const nonNullCustomerCode = CustomerContext.getCustomerCode();
        const cacheKey = this.getCacheKey(enumClassNameToUse, moduleCode);
        let promise: Promise<AxiosResponse<CodeDescriptionRestDto[]>> =
            CacheService.getInstance().get(cacheKey, nonNullCustomerCode);

        if (promise !== null) {
            const result: AxiosResponse<CodeDescriptionRestDto[]> = await promise;
            return result.data;
        }

        promise = this.masterEnumApi.getMasterEnumValuesByClassname(moduleCode, enumClassNameToUse);

        CacheService.getInstance().set(cacheKey, promise, nonNullCustomerCode);

        const result: AxiosResponse<CodeDescriptionRestDto[]> = await promise;
        return result.data;
    }

    public async getCodeDescriptionByCodeFromEnumByClassname(enumClassName: string, code: string, moduleCode: string): Promise<CodeDescriptionRestDto | null> {
        const enumValues: CodeDescriptionRestDto[] = await this.getEnumValuesByClassName(enumClassName, moduleCode);
        return enumValues.find(value => value.code === code) ?? null;
    }

    public async getCmsEnumValuesByClassname(enumClassNameParam: EnumClassNameCmsEnum): Promise<CodeDescriptionRestDto[]> {
        return this.getEnumValuesByClassName(enumClassNameParam, DpModuleEnum.CMS);
    }

    public async getCpcEnumValuesByClassname(enumClassName: EnumClassNameCpcEnum): Promise<CodeDescriptionRestDto[]> {
        return this.getEnumValuesByClassName(enumClassName, DpModuleEnum.CPC);
    }

    public async getOrderEnumValuesByClassname(enumClassName: EnumClassNameOrderEnum): Promise<CodeDescriptionRestDto[]> {
        return this.getEnumValuesByClassName(enumClassName, DpModuleEnum.ORDER);
    }

    public async getPimEnumValuesByClassname(enumClassName: EnumClassNamePimEnum): Promise<CodeDescriptionRestDto[]> {
        return this.getEnumValuesByClassName(enumClassName, DpModuleEnum.PIM);
    }

    public async getOrderEnumDescription(enumClassName: EnumClassNameOrderEnum, enumCode: string): Promise<string> {
        const values = await this.getOrderEnumValuesByClassname(enumClassName);

        for (const val of values) {
            if (val.code === enumCode) {
                return (val.description === undefined || val.description === null ? '' : val.description);
            }
        }

        return '';
    }

    public async getOrderEnumContentForType(pharmacyCode: string | null, enumClassName: string, enumCode: string, typeCode: string): Promise<EnumContentRestDtoModel> {
        const response = await this.orderEnumApi.getOrderEnumContentForType(enumClassName, enumCode, typeCode, null, pharmacyCode);

        return response.data;
    }

    public async updateOrderEnumContentForType(pharmacyCode: string | null, content: MultilingualFieldRestDto, enumClassName: string, enumCode: string, typeCode: string, statusCode: StatusEnum | null): Promise<void> {
        const updateDto = UpdateEnumContentRestDtoModel.createWithDefaults()
        updateDto.status_code = statusCode;
        updateDto.pharmacy_code = pharmacyCode
        updateDto.content = content;

        await this.orderEnumApi.updateOrderEnumContentForType(updateDto, enumClassName, enumCode, typeCode);
    }

    public async updateOrderEnumFallbackContentForType(content: MultilingualFieldRestDto, enumClassName: string, enumCode: string, typeCode: string, statusCode: StatusEnum | null): Promise<void> {
        const updateDto = UpdateEnumContentRestDtoModel.createWithDefaults()
        updateDto.status_code = statusCode;
        updateDto.content = content;

        await this.orderEnumApi.updateOrderEnumFallbackContentForType(updateDto, enumClassName, enumCode, typeCode);
    }

    private getCacheKey(enumClassName: string, moduleCode: string): string {
        return CacheKeyEnum.ENUM + '_' + moduleCode + '_' + enumClassName;
    }
}
